/** The fields of an in-app notification (live toast or persisted) that decide where a click goes. */
export interface NotificationRouteSource {
  type?: string | undefined
  entityType?: string | undefined
  entityId?: string | undefined
  metadata?: Record<string, unknown> | undefined
}

/**
 * The in-app path a notification opens, or null when it points at nothing.
 *
 * - `wa_conversation` (a WhatsApp reply): that thread in the Inbox (R30).
 * - `outreach_reply`: the contact's email conversation. `entityId` is the CONTACT id; the legacy
 *   `/outreach/inbox` URL redirects into the Inbox, which opens the contact's newest thread.
 * - `outreach_campaign` (a campaign was auto-paused): the campaign page, not a contact page with a
 *   campaign id.
 * - `organization` ("you were added to ..."): nothing to open. The notification belongs to that
 *   organization, so you are already in it when you see it.
 * - a record: `deal` → the deal page, `company` → the company page (mentions in a company note),
 *   anything else → the contact page.
 */
export function notificationTarget(n: NotificationRouteSource): string | null {
  if (!n.entityType || !n.entityId) return null
  if (n.entityType === 'wa_conversation') return `/inbox?thread=wa:${n.entityId}`
  if (n.entityType === 'outreach_reply') return `/outreach/inbox?contactId=${n.entityId}`
  if (n.entityType === 'outreach_campaign') return `/outreach/campaigns/${n.entityId}`
  if (n.entityType === 'organization') return null
  const basePath = n.entityType === 'deal' ? '/deals' : n.entityType === 'company' ? '/companies' : '/contacts'
  return `${basePath}/${n.entityId}`
}
